import React, { useState, useEffect } from 'react';
import usePWAInstall from '../../hooks/usePWAInstall';
import PWAInstallInstructions from './PWAInstallInstructions';

const DISMISS_KEY = 'uttaapp_pwa_prompt_dismissed_at';
// Re-show the auto popup again after this many days if the user dismissed it
// (and still hasn't installed). Set to 0 to show on every visit.
const SNOOZE_DAYS = 3;
// Small delay before the popup appears so it doesn't fight with first paint.
const SHOW_DELAY_MS = 2500;

const isSnoozed = () => {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!ts) return false;
    const elapsed = Date.now() - ts;
    return elapsed < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

// Auto-appearing, beautiful, fully responsive install popup.
// - Mobile: bottom sheet that slides up.
// - Desktop: floating card pinned to the bottom-right.
const PWAInstallPrompt = () => {
  const { installable, canPrompt, isIOS, promptInstall } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    if (!installable) {
      setVisible(false);
      return undefined;
    }
    if (isSnoozed()) return undefined;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [installable]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {
      /* ignore storage errors */
    }
  };

  const handleInstall = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      setVisible(false);
      if (outcome === 'accepted') {
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 4000);
      } else if (outcome === 'unavailable') {
        setShowInstructions(true);
      }
      return;
    }
    setVisible(false);
    setShowInstructions(true);
  };

  return (
    <>
      {visible && installable && (
        <div className="fixed inset-x-0 bottom-0 z-[9999] w-full px-4 pb-4 animate-pwa-up md:inset-x-auto md:bottom-6 md:right-6 md:w-[380px] md:px-0 md:pb-0">
          <div className="mx-auto max-w-md overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-[#0f766e] to-[#065f46] p-5 shadow-2xl md:max-w-none">
            <div className="mb-3 flex items-center">
              <div className="mr-3 flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/15 p-1.5">
                <img src="/icon-192.png" alt="UttaApp" className="h-full w-full rounded-xl object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-white">Install UttaApp</h3>
                <p className="text-xs text-white/80">Fast, app-like, works offline</p>
              </div>
              <button
                onClick={dismiss}
                className="ml-2 rounded-full p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="mb-4 pl-1 text-sm leading-relaxed text-white/90">
              Add UttaApp to your home screen for one-tap booking, instant ride
              notifications and a smoother experience.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleInstall}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white py-3.5 px-4 font-bold text-[#0f766e] shadow-lg transition-all hover:bg-gray-50 active:bg-gray-100"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isIOS ? 'How to install' : 'Install Now'}
              </button>
              <button
                onClick={dismiss}
                className="rounded-xl border-2 border-white/60 px-6 py-3.5 font-semibold text-white transition-all hover:bg-white/10 active:bg-white/20"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {showInstructions && (
        <PWAInstallInstructions isIOS={isIOS} onClose={() => setShowInstructions(false)} />
      )}

      {showSnackbar && (
        <div className="fixed top-5 left-1/2 z-[10002] w-[90%] max-w-md -translate-x-1/2 animate-pwa-down">
          <div className="flex items-center justify-between rounded-xl bg-[#0f766e] px-4 py-3.5 text-white shadow-2xl">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-semibold">UttaApp installed! Find it on your home screen.</span>
            </div>
            <button onClick={() => setShowSnackbar(false)} className="ml-3 shrink-0 text-white/90 hover:text-white" aria-label="Close">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pwa-up { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pwa-down { from { opacity: 0; transform: translate(-50%, -100%); } to { opacity: 1; transform: translate(-50%, 0); } }
        .animate-pwa-up { animation: pwa-up 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-pwa-down { animation: pwa-down 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
